import mixpanel from 'mixpanel-browser';
import { track } from './mixpanelTracking';

/**
 * Enhanced screen recording and session replay functionality for Mixpanel
 */

// Configuration for screen recording
const DEFAULT_CONFIG = {
  captureRate: 1, // frames per second
  quality: 0.6,   // compression quality (0-1)
  maxDuration: 300, // max recording duration in seconds (5 minutes)
  includeAudio: false,
  recordConsole: true,
  recordNetwork: true,
  maskSensitiveElements: true,
  sensitiveSelectors: [
    'input[type="password"]',
    '[data-mp-mask]',
    '.sensitive',
    '.private-data'
  ]
};

// Store for recording state
let recordingState = {
  isRecording: false,
  startTime: null,
  recordingId: null,
  frames: [],
  events: [],
  config: DEFAULT_CONFIG,
  recordingInterval: null
};

/**
 * Initialize screen recording functionality
 * @param {Object} config - Configuration options
 */
export const initScreenRecording = (config = {}) => {
  recordingState.config = { ...DEFAULT_CONFIG, ...config };
  
  // Set up console recording if enabled
  if (recordingState.config.recordConsole) {
    setupConsoleRecording();
  }
  
  // Set up network recording if enabled
  if (recordingState.config.recordNetwork) {
    setupNetworkRecording();
  }
  
  // Add privacy masking
  if (recordingState.config.maskSensitiveElements) {
    addPrivacyMasking();
  }
  
  // Check if native Mixpanel session recording is enabled
  if (mixpanel && mixpanel._config && mixpanel._config.record_sessions_percent > 0) {
    console.log('✅ Using native Mixpanel session recording');
    
    // If using native recording, we'll use it for automatic session recording
    // but keep our custom implementation for manual control
    if (typeof mixpanel.session_recording === 'object' && 
        typeof mixpanel.session_recording.start === 'function') {
      // Native session recording API is available
      console.log('✅ Native Mixpanel session recording API detected');
    } else {
      console.log('ℹ️ Native Mixpanel session recording configured but API not detected');
    }
  } else {
    console.log('ℹ️ Using custom session recording implementation');
  }
  
  console.log('✅ Mixpanel screen recording initialized');
};

/**
 * Start recording the user's screen session
 * @returns {string} Recording ID
 */
export const startScreenRecording = () => {
  if (recordingState.isRecording) {
    console.warn('Screen recording already in progress');
    return recordingState.recordingId;
  }
  
  const recordingId = `rec_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  
  // Check if native Mixpanel session recording is available
  const hasNativeRecording = mixpanel && 
    typeof mixpanel.session_recording === 'object' && 
    typeof mixpanel.session_recording.start === 'function';
  
  // Start native recording if available
  if (hasNativeRecording) {
    try {
      mixpanel.session_recording.start();
      console.log('✅ Native Mixpanel session recording started');
    } catch (error) {
      console.error('Error starting native Mixpanel session recording:', error);
    }
  }
  
  recordingState = {
    ...recordingState,
    isRecording: true,
    startTime: Date.now(),
    recordingId,
    frames: [],
    events: []
  };
  
  // Track recording start
  track('Screen Recording Started', {
    recording_id: recordingId,
    config: recordingState.config,
    using_native_api: hasNativeRecording
  });
  
  // If we're not using native recording or want additional data, set up our custom recording
  if (!hasNativeRecording || recordingState.config.recordConsole || recordingState.config.recordNetwork) {
    // Set up interval for capturing frames
    const captureInterval = Math.floor(1000 / recordingState.config.captureRate);
    recordingState.recordingInterval = setInterval(() => {
      captureScreenFrame();
      
      // Check if we've reached max duration
      const elapsedTime = (Date.now() - recordingState.startTime) / 1000;
      if (elapsedTime >= recordingState.config.maxDuration) {
        stopScreenRecording();
      }
    }, captureInterval);
  }
  
  return recordingId;
};

/**
 * Stop the current screen recording
 * @returns {Object} Recording data
 */
export const stopScreenRecording = () => {
  if (!recordingState.isRecording) {
    console.warn('No screen recording in progress');
    return null;
  }
  
  // Check if native Mixpanel session recording is available
  const hasNativeRecording = mixpanel && 
    typeof mixpanel.session_recording === 'object' && 
    typeof mixpanel.session_recording.stop === 'function';
  
  // Stop native recording if available
  if (hasNativeRecording) {
    try {
      mixpanel.session_recording.stop();
      console.log('✅ Native Mixpanel session recording stopped');
    } catch (error) {
      console.error('Error stopping native Mixpanel session recording:', error);
    }
  }
  
  // Clear recording interval for custom recording
  if (recordingState.recordingInterval) {
    clearInterval(recordingState.recordingInterval);
  }
  
  const recordingData = {
    recording_id: recordingState.recordingId,
    start_time: recordingState.startTime,
    end_time: Date.now(),
    duration_ms: Date.now() - recordingState.startTime,
    frame_count: recordingState.frames.length,
    event_count: recordingState.events.length,
    using_native_api: hasNativeRecording
  };
  
  // Track recording end
  track('Screen Recording Ended', recordingData);
  
  // Send custom recording data to Mixpanel if we have any
  if (recordingState.frames.length > 0 || recordingState.events.length > 0) {
    sendRecordingToMixpanel(recordingState.recordingId);
  }
  
  // Reset recording state
  recordingState.isRecording = false;
  
  return recordingData;
};

/**
 * Capture a single frame of the screen
 * @private
 */
const captureScreenFrame = () => {
  if (!recordingState.isRecording) return;
  
  try {
    // In a real implementation, this would use more advanced techniques
    // For now, we'll just capture the DOM state
    const timestamp = Date.now();
    const frame = {
      timestamp,
      relative_time: timestamp - recordingState.startTime,
      url: window.location.href,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      scroll_x: window.scrollX,
      scroll_y: window.scrollY
    };
    
    recordingState.frames.push(frame);
  } catch (error) {
    console.error('Error capturing screen frame:', error);
  }
};

/**
 * Set up console recording
 * @private
 */
const setupConsoleRecording = () => {
  const originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error
  };
  
  // Override console methods
  console.log = function(...args) {
    if (recordingState.isRecording) {
      recordConsoleEvent('log', args);
    }
    originalConsole.log.apply(console, args);
  };
  
  console.warn = function(...args) {
    if (recordingState.isRecording) {
      recordConsoleEvent('warn', args);
    }
    originalConsole.warn.apply(console, args);
  };
  
  console.error = function(...args) {
    if (recordingState.isRecording) {
      recordConsoleEvent('error', args);
    }
    originalConsole.error.apply(console, args);
  };
};

/**
 * Record a console event
 * @private
 */
const recordConsoleEvent = (type, args) => {
  try {
    const event = {
      type: 'console',
      console_type: type,
      timestamp: Date.now(),
      relative_time: Date.now() - recordingState.startTime,
      message: args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch (e) {
            return '[Object]';
          }
        }
        return String(arg);
      }).join(' ')
    };
    
    recordingState.events.push(event);
  } catch (error) {
    // Silently fail to avoid infinite loops
  }
};

/**
 * Set up network request recording
 * @private
 */
const setupNetworkRecording = () => {
  const originalFetch = window.fetch;
  
  // Override fetch
  window.fetch = async function(resource, options) {
    if (!recordingState.isRecording) {
      return originalFetch.apply(window, [resource, options]);
    }
    
    const startTime = Date.now();
    const url = typeof resource === 'string' ? resource : resource.url;
    
    try {
      const response = await originalFetch.apply(window, [resource, options]);
      
      // Record successful request
      recordNetworkEvent({
        url,
        method: options?.method || 'GET',
        status: response.status,
        duration: Date.now() - startTime,
        success: response.ok
      });
      
      return response;
    } catch (error) {
      // Record failed request
      recordNetworkEvent({
        url,
        method: options?.method || 'GET',
        error: error.message,
        duration: Date.now() - startTime,
        success: false
      });
      
      throw error;
    }
  };
  
  // Similar overrides could be added for XMLHttpRequest
};

/**
 * Record a network event
 * @private
 */
const recordNetworkEvent = (details) => {
  try {
    const event = {
      type: 'network',
      timestamp: Date.now(),
      relative_time: Date.now() - recordingState.startTime,
      ...details
    };
    
    recordingState.events.push(event);
  } catch (error) {
    // Silently fail
  }
};

/**
 * Add privacy masking for sensitive elements
 * @private
 */
const addPrivacyMasking = () => {
  // Add CSS to mask sensitive elements
  const style = document.createElement('style');
  style.textContent = `
    ${recordingState.config.sensitiveSelectors.join(', ')} {
      -webkit-text-security: disc !important;
      color: transparent !important;
      text-shadow: 0 0 8px rgba(0,0,0,0.5) !important;
    }
  `;
  document.head.appendChild(style);
};

/**
 * Send recording data to Mixpanel
 * @private
 */
const sendRecordingToMixpanel = (recordingId) => {
  if (!mixpanel) return;
  
  // In a real implementation, this would compress and chunk the data
  // For now, we'll just send summary data
  
  const recordingSummary = {
    recording_id: recordingId,
    start_time: recordingState.startTime,
    end_time: Date.now(),
    duration_ms: Date.now() - recordingState.startTime,
    frame_count: recordingState.frames.length,
    event_count: recordingState.events.length,
    urls_visited: [...new Set(recordingState.frames.map(f => f.url))],
    console_error_count: recordingState.events.filter(e => e.type === 'console' && e.console_type === 'error').length,
    network_error_count: recordingState.events.filter(e => e.type === 'network' && !e.success).length
  };
  
  // Track as a Mixpanel event
  track('Session Recording', recordingSummary);
};

export default {
  initScreenRecording,
  startScreenRecording,
  stopScreenRecording
};
